/**
 * Krok FIX6 bod 7 — SLOUPEC „DALŠÍ KROK" V ZAKÁZKÁCH.
 *
 * Nález ze živého ověření 2. 10. 2026: v tabulce `/zakazky` byl sloupec
 * „Další krok" u VŠECH 100 zakázek „—", přitom detail zakázky další krok
 * ukazuje. Příčina není chybějící výpočet, ale DVĚ RŮZNÉ TABULKY:
 *
 *  - `CaseDto.nextTaskTitle` (server, `packages/case/.../service.ts`
 *    `CASES_FROM`) se počítá z `case_tasks`,
 *  - úkoly makléře ale aplikace zapisuje do `crm_tasks` — `case_tasks` je
 *    mrtvá cesta, přesně jak to pojmenoval už DEMO1 seed
 *    (`scripts/db/demo1-seed.sql` BLOK 7: „`crm_tasks`, ne `case_tasks` (ten
 *    druhý je mrtvá/nevyužitá cesta — Dnešek i tabule čtou `/v1/crm/tasks*`)").
 *    Měřeno na preview: `case_tasks` demo kanceláře = 0 řádků, tedy sloupec
 *    nemohl ukázat nic jiného než „—".
 *
 * `crm_tasks` nečte server v kontextu `case` ZÁMĚRNĚ — je to jiný kontext
 * (CLAUDE.md zlaté pravidlo 4, hranice kontextů). Obrazovka si proto úkoly
 * dotáhne jedním dávkovým voláním `/v1/crm/tasks` (stejný vzor jako náklady
 * a aktuálnost výše na téže stránce) a spáruje je podle `caseId` tady —
 * v čisté funkci, aby na to šel napsat test bez prohlížeče.
 */

import { isTaskOpen } from './task-board.js';

/** Jen to, co párování potřebuje — ne celý `Task` z API. */
export interface NextStepTaskInput {
  readonly caseId?: string | null | undefined;
  readonly title: string;
  readonly status: string;
  readonly dueAt?: string | null | undefined;
  /**
   * Krok UXF5 bod 5 (nalez #12): KDO ten krok ma udelat. Re-audit GPT:
   * „ve sloupci Další krok chybí samostatný termín a odpovědný člověk" —
   * veta bez termínu a vlastnika se neda naplanovat.
   */
  readonly ownerUserId?: string | null | undefined;
}

/**
 * „Otevřený" se NEPOČÍTÁ tady — bere se `isTaskOpen` z `task-board.ts`.
 *
 * Do opravy 1 kroku TAB1 tu stál vlastní výčet `{todo, doing}`. Když TAB1
 * přidal čtvrtý sloupec „Čeká na druhé", rozešel se s hlavičkou tabule, která
 * `waiting` za otevřený počítá — zakázka čekající na klienta tím přišla
 * o větu „co brzdí". Dvě kopie téže pravdy, přesně jak to popisuje
 * `docs/platform/TESTOVANI.md` pravidlo 2.
 */

/**
 * Nejbližší otevřený úkol pro každou zakázku.
 *
 * Řazení je TOTÉŽ jako na serveru u `case_tasks` (`service.ts CASES_FROM`):
 * nejdřív úkoly S termínem podle termínu, úkoly bez termínu až nakonec —
 * jinak by „další krok" v tabulce a v detailu ukazoval jiný úkol.
 */
export function nextStepByCase(
  tasks: readonly NextStepTaskInput[],
): ReadonlyMap<string, string> {
  return new Map([...nextStepTaskByCase(tasks)].map(([caseId, task]) => [caseId, task.title]));
}

/**
 * Krok UXF5 bod 5 (nalez #12) — TENTYZ VYBER, ale celym ukolem: sloupec
 * „Další krok" potrebuje vedle vety i TERMIN a ODPOVEDNEHO MAKLERE.
 *
 * `nextStepByCase` z toho jen vybira titul, takze tabulka a karta nemohou
 * ukazovat jiny ukol nez tenhle (druhy vyber „nejblizsiho" by se po prvni
 * zmene razeni rozesel).
 */
export function nextStepTaskByCase(
  tasks: readonly NextStepTaskInput[],
): ReadonlyMap<string, NextStepTaskInput> {
  const best = new Map<string, NextStepTaskInput>();
  for (const task of tasks) {
    const caseId = task.caseId ?? null;
    if (caseId === null || caseId === '') continue;
    if (!isTaskOpen(task.status)) continue;
    const current = best.get(caseId);
    if (current === undefined || isSooner(task, current)) best.set(caseId, task);
  }
  return best;
}

function isSooner(candidate: NextStepTaskInput, current: NextStepTaskInput): boolean {
  const a = candidate.dueAt ?? null;
  const b = current.dueAt ?? null;
  if (a === null && b === null) return false;
  if (a === null) return false;
  if (b === null) return true;
  return new Date(a).getTime() < new Date(b).getTime();
}
